import { atom } from 'recoil';
import { AI_CHAT_PANEL_WIDTH } from '../constants';
import type { ChatIntegration, ChatMode } from '../components/AiChat/types';
import type { ChatStarterKey } from '../components/AiChat/constants';
import { TreeViews } from '../hooks/treeData/types';
import { TreeViewNode } from '../components/TreeView/TreeView';
import type {
  AgentContext,
  AgentOrigin,
  AgentTestConfigPlan,
  AgentCodeTestConfigPlan,
  AgentCustomSqlTestConfigPlan,
  AgentMergeIncidentsPlan,
} from '../types';

const localStorageEffect =
  (key: string) =>
  ({ setSelf, onSet }: any) => {
    const savedValue = localStorage.getItem(key);
    if (savedValue != null) {
      setSelf(JSON.parse(savedValue));
    }

    onSet((newValue: any, _: any, isReset: any) =>
      isReset
        ? localStorage.removeItem(key)
        : localStorage.setItem(key, JSON.stringify(newValue)),
    );
  };

export const currentTreeData = atom<TreeViews | undefined>({
  key: 'currentTreeData',
  default: undefined,
});

export const currentTreeNodesById = atom<Record<string, TreeViewNode>>({
  key: 'currentTreeNodesById',
  default: {},
});

export const treeViewCollapsed = atom<{
  isCollapsed: boolean;
  userOverride?: boolean;
}>({
  key: 'treeViewCollapsed',
  default: { isCollapsed: false, userOverride: undefined },
});

export const navbarCollapsed = atom<{
  isCollapsed: boolean;
  userOverride?: boolean;
}>({
  key: 'navbarCollapsed',
  default: { isCollapsed: false, userOverride: undefined },
  effects: [localStorageEffect('is_navbar_collapsed')],
});

export const selectedEnvironmentId = atom<string>({
  key: 'selectedEnvironmentId',
  default: undefined,
  effects: [localStorageEffect('selected_environment_id')],
});

export type AiChatPanelConfig = {
  startMessage?: string;
  starterKey?: ChatStarterKey;
  initialThreadId?: string;
  focusToolCallId?: string;
  origin?: AgentOrigin;
};

export type AiChatPanelAtomValue = {
  isExpanded: boolean;
  isFullWidth: boolean;
  threadLoadError?: boolean;
  width: number;
  initialConfig?: AiChatPanelConfig;
  context?: AgentContext;
  selectedMode?: ChatMode;
  enabledIntegrations?: ChatIntegration[];
};

export const aiChatPanel = atom<AiChatPanelAtomValue>({
  key: 'aiChatPanel',
  default: { isExpanded: true, isFullWidth: false, width: AI_CHAT_PANEL_WIDTH },
});

export const agentCloudTestConfigPlan = atom<AgentTestConfigPlan | null>({
  key: 'agentCloudTestConfigPlan',
  default: null,
});

export const agentCodeTestConfigPlan = atom<AgentCodeTestConfigPlan | null>({
  key: 'agentCodeTestConfigPlan',
  default: null,
});

export const agentCustomSqlTestConfigPlan =
  atom<AgentCustomSqlTestConfigPlan | null>({
    key: 'agentCustomSqlTestConfigPlan',
    default: null,
  });

export const agentMergeIncidentsPlan = atom<AgentMergeIncidentsPlan | null>({
  key: 'agentMergeIncidentsPlan',
  default: null,
});
